import { SelectOptions } from '@spo/logic';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Select, {
  ActionMeta,
  InputActionMeta,
  MultiValue as mv,
} from 'react-select';
import { useSearchRetailFamily } from '../../../hooks/useRetailFamily';
import { arrayUniqueByKey } from '../../../utils/array';
import { metaActions } from '../../../utils/metaActions';
import { selectFilterStyles } from '../common/layout';
import { concatOptions, sortOptions } from '../common/options';
import { IconOption, MultiValue } from '../custom/CustomSelectInput';
import { RetailFamiliesSearchInputProps } from './RetailFamiliesSearchInputProps';

export const RetailFamiliesSearchInput = ({
  onChange,
  date,
  selectedRetailFamilyOptions = [],
  disabled,
}: RetailFamiliesSearchInputProps) => {
  const { t } = useTranslation();

  const [retailFamiliesOptions, setRetailFamiliesOptions] = useState<
    SelectOptions[]
  >([]);
  const [selectedRetailFamilies, setSelectedRetailFamilies] =
    useState<mv<SelectOptions> | null>(null);
  const [searchedRetailFamily, setSearchedRetailFamily] = useState<string>('');

  const {
    data: retailFamilyData,
    isFetching: isRetailFamilyFetching,
    error: retailFamilyError,
  } = useSearchRetailFamily(
    searchedRetailFamily,
    date,
    searchedRetailFamily.length > 0,
  );

  const setOptionsRetailFamiliesAndSelectedRetailFamilies = (
    retailFamiliesListOptions: SelectOptions[],
  ) => {
    const sortedRetailFamiliesListOptions = sortOptions(
      [],
      retailFamiliesListOptions,
    );

    const uniqueFamiliesOptions = [
      ...arrayUniqueByKey<SelectOptions>(
        sortedRetailFamiliesListOptions,
        'value',
      ),
    ];
    setRetailFamiliesOptions(uniqueFamiliesOptions);

    selectedRetailFamilyOptions.length > 0 &&
      setSelectedRetailFamilies([...new Set(selectedRetailFamilyOptions)]);
  };

  useEffect(() => {
    if (retailFamilyData && retailFamilyData.length > 0) {
      const retailFamiliesListOptions: SelectOptions[] = (
        retailFamilyData ?? []
      ).map((retailFamily) => ({
        label: `${retailFamily.retailFamilyId} - ${retailFamily.name}`,
        value: Number(retailFamily.retailFamilyId),
        name: `${retailFamily.name}`,
      }));

      // eslint-disable-next-line react-hooks/set-state-in-effect
      setOptionsRetailFamiliesAndSelectedRetailFamilies(
        retailFamiliesListOptions,
      );
    }
  }, [retailFamilyData]);

  useEffect(() => {
    if (retailFamilyError) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setOptionsRetailFamiliesAndSelectedRetailFamilies([
        {
          label: t(`select.loading_error`) ?? '',
          value: NaN,
          name: '',
        },
      ]);
    }
  }, [retailFamilyError]);

  useEffect(() => {
    if (selectedRetailFamilyOptions.length > 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setOptionsRetailFamiliesAndSelectedRetailFamilies(
        selectedRetailFamilyOptions,
      );
    }
  }, []);

  const onInputChange = (search: string, actionMeta: InputActionMeta) => {
    if (actionMeta.action === metaActions.inputChange) {
      setSearchedRetailFamily(search);
    }
  };

  const handleChange = async (
    retailFamilies: mv<SelectOptions>,
    actionMeta: ActionMeta<SelectOptions>,
  ) => {
    const sortedRetailFamiliesOptions = sortOptions(
      retailFamilies,
      retailFamiliesOptions,
    );
    setRetailFamiliesOptions(sortedRetailFamiliesOptions);

    const allSelectedRetailFamilies = concatOptions(
      actionMeta,
      selectedRetailFamilies,
      retailFamilies,
    );

    onChange(allSelectedRetailFamilies);
    setSelectedRetailFamilies(allSelectedRetailFamilies);
  };

  return (
    <div
      className="vtmn-w-full vtmn-mt-5"
      data-testid="retail-families_input_search"
    >
      <span className="vtmn-typo_text-2">
        {t('input.retail-families_label')}
      </span>
      <Select
        data-testid="retail-families-search-input"
        aria-label={
          t(`select.retail-families_placeholder`) ||
          'Add one or several retail families'
        }
        className="vtmn-mt-2"
        styles={selectFilterStyles}
        placeholder={t(`select.retail-families_placeholder`)}
        isMulti
        value={selectedRetailFamilies}
        onChange={handleChange}
        onInputChange={onInputChange}
        isDisabled={disabled}
        isSearchable
        hideSelectedOptions={false}
        options={retailFamiliesOptions}
        isLoading={isRetailFamilyFetching}
        closeMenuOnSelect={false}
        components={{
          Option: IconOption,
          MultiValue,
        }}
        id="retail-families-filter"
        menuPlacement="top"
      />
    </div>
  );
};
